import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { useMutation } from "@tanstack/react-query";
import type { FC } from "react";
import { queryClient } from "src/query-client";
import theme from "src/styles/theme";
import { showErrorToast, showSuccessToast } from "src/utils/toast";
import { migrateVendor } from "../api-v2/vendor-migration";
import css from "src/styles/dialog.module.css";

export interface MigrateToAppDialogProps {
  vendorId: string | number;
  vendorName: string;
  onSuccess: () => void;
  onClose: () => void;
}

export const MigrateToAppDialog: FC<MigrateToAppDialogProps> = ({ vendorId, vendorName, onSuccess, onClose }) => {
  const mutation = useMutation(
    {
      mutationKey: ["migrate-vendor", vendorId],
      mutationFn: () => migrateVendor(vendorId),
      onSuccess() {
        showSuccessToast("Migration request sent successfully");
        onSuccess();
        onClose();
      },
      onError(error) {
        console.error("Failed to request vendor migration:", error);
        showErrorToast(`Failed to request migration - ${error.message}`);
      },
    },
    // The dependency we use for confirmation dialogs, `react-confirm-alert`, wraps our custom UI inside a
    // separate React context. We must pass the query client manually.
    queryClient,
  );

  return (
    <ThemeProvider theme={theme}>
      <div className={css.dialog}>
        <div className={css.content}>
          <p className={css.title}>Are you sure you want to migrate "{vendorName}" to the app?</p>
          <p className={css.paragraph}>
            All active stores for this vendor will be sunset effective today, and a migration request will be submitted
            for each store.
          </p>
          <p>By selecting Migrate to App you are confirming you have:</p>
          <ul>
            <li>Communicated any trade offs (e.g. custom scopes, custom shortage lag)</li>
            <li>
              Obtained an updated contract OR written authorization to pursue Overages and Vendor Returns on the SPS RR
              App
            </li>
            <li>You have run an audit to update eACV</li>
          </ul>
          <p>If you are ready to proceed, please click Migrate to App. This cannot be undone once selected.</p>
        </div>

        <div className={css.buttonGroup}>
          <Button onClick={onClose} className={css.button} disabled={mutation.isPending} variant="text">
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            className={css.button}
            variant="contained"
            color="primary"
            disabled={mutation.isPending}
            type="submit"
          >
            Migrate to App
            {mutation.isPending && <CircularProgress size={14} style={{ marginLeft: "12px" }} />}
          </Button>
        </div>
      </div>
    </ThemeProvider>
  );
};
